import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, Loader2, Paperclip, Reply, Edit2, X, Trash2, Download, FileText } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtRelative, fmtDateTime } from "../lib/format.js";
import { useAuth } from "../lib/auth.jsx";

const MAX_LEN = 2000;
const EDIT_WINDOW_MS = 15 * 60 * 1000;

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

const ROLE_COLORS = {
  admin: "#1F3864",
  service_advisor: "#0D9488",
  workshop_supervisor: "#B45309",
  workshop_coordinator: "#7C3AED",
  parts_user: "#28A745",
  classifier: "#F59E0B",
  department_manager: "#DC3545",
  national_aftersales_manager: "#1F3864",
  reception: "#6C757D",
  read_only_viewer: "#94A3B8",
};

function MentionDropdown({ users, onPick, query }) {
  if (!users || users.length === 0) return null;
  return (
    <div className="absolute left-2 bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg w-64 max-h-48 overflow-y-auto z-30" data-testid="mention-dropdown">
      <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#6C757D] border-b border-gray-100">
        @{query || "…"}
      </div>
      {users.map((u) => (
        <button
          key={u.id}
          type="button"
          onClick={() => onPick(u)}
          className="w-full text-left px-2 py-1.5 hover:bg-[#F4F6F8] flex items-center gap-2"
          data-testid={`mention-pick-${u.id}`}
        >
          <span
            className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
            style={{ backgroundColor: ROLE_COLORS[u.role] || "#6C757D" }}
          >
            {initials(u.name)}
          </span>
          <span className="text-sm text-[#1A1A2E]">{u.name}</span>
          <span className="text-[10px] text-[#94A3B8] ml-auto">{u.role}</span>
        </button>
      ))}
    </div>
  );
}

function CommentEditor({ recordType, recordId, parentId, onPosted, onCancel, initialBody = "", commentId }) {
  const [body, setBody] = useState(initialBody);
  const [mentionUserIds, setMentionUserIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const textareaRef = useRef(null);

  // Mention typeahead
  const onChange = (e) => {
    const val = e.target.value;
    if (val.length > MAX_LEN) return;
    setBody(val);
    // Detect '@token' immediately before cursor
    const cursor = e.target.selectionStart;
    const tail = val.slice(0, cursor);
    const m = tail.match(/(?:^|\s)@([^\s@]{0,30})$/);
    setMentionQuery(m ? m[1] : null);
  };

  useEffect(() => {
    if (mentionQuery == null) {
      setMentionResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/mentionable?q=${encodeURIComponent(mentionQuery)}&record_type=${recordType}&record_id=${recordId}`);
        setMentionResults(data);
      } catch {
        /* silent — typeahead failures are non-critical */
      }
    }, 150);
    return () => clearTimeout(id);
  }, [mentionQuery, recordType, recordId]);

  const pickMention = (u) => {
    setBody((b) => {
      const cursor = textareaRef.current?.selectionStart ?? b.length;
      const before = b.slice(0, cursor);
      const after = b.slice(cursor);
      const replaced = before.replace(/@([^\s@]{0,30})$/, `@${u.name} `);
      return replaced + after;
    });
    setMentionUserIds((ids) => (ids.includes(u.id) ? ids : [...ids, u.id]));
    setMentionQuery(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > 5) {
      setErr("Máximo 5 ficheiros por comentário");
      e.target.value = "";
      return;
    }
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        setErr(`Ficheiro "${f.name}" excede 10 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("record_type", recordType);
      fd.append("record_id", recordId);
      try {
        const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
      } catch (ex) {
        setErr(formatError(ex.response?.data?.detail) || "Falha no upload");
      }
    }
    e.target.value = "";
  };

  const submit = async () => {
    setErr("");
    if (!body.trim()) return;
    setBusy(true);
    try {
      if (commentId) {
        const { data } = await api.patch(`/comments/${commentId}`, { body: body.trim(), mention_user_ids: mentionUserIds });
        onPosted(data);
      } else {
        const { data } = await api.post("/comments", {
          record_type: recordType,
          record_id: recordId,
          body: body.trim(),
          parent_comment_id: parentId || null,
          attachment_ids: attachments.map((a) => a.id),
          mention_user_ids: mentionUserIds,
        });
        onPosted(data);
      }
      setBody("");
      setAttachments([]);
      setMentionUserIds([]);
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || "Falha ao publicar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid="comment-editor">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={onChange}
        placeholder="Escreva um comentário… escreva @ para mencionar alguém"
        rows={parentId ? 2 : 3}
        className="w-full border border-gray-300 rounded-sm p-2 text-sm focus:outline-none focus:border-[#1F3864]"
        data-testid="comment-textarea"
      />
      {mentionQuery != null && mentionResults.length > 0 && (
        <MentionDropdown users={mentionResults} onPick={pickMention} query={mentionQuery} />
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {attachments.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 bg-[#F4F6F8] text-xs px-2 py-0.5 rounded-sm">
              <Paperclip className="w-3 h-3" /> {a.original_filename}
              <button
                type="button"
                onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))}
                className="text-[#DC3545] hover:underline ml-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {err && (
        <div className="text-xs text-[#DC3545] mt-1" data-testid="comment-error">{err}</div>
      )}
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {!commentId && (
            <label className="cursor-pointer text-[#6C757D] hover:text-[#1F3864]" title="Anexar ficheiros">
              <Paperclip className="w-4 h-4" />
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx" onChange={onPickFile} className="hidden" data-testid="comment-attach-input" />
            </label>
          )}
          <span className={`text-[10px] ${body.length > MAX_LEN * 0.9 ? "text-[#DC3545]" : "text-[#94A3B8]"}`}>
            {body.length}/{MAX_LEN}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onCancel && (
            <button onClick={onCancel} className="text-xs px-2 py-1 text-[#6C757D] hover:text-[#1A1A2E]" type="button">Cancelar</button>
          )}
          <button
            onClick={submit}
            disabled={busy || !body.trim()}
            className="inline-flex items-center gap-1 text-xs bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm font-medium disabled:opacity-50"
            data-testid={commentId ? "comment-save-edit" : "comment-publish"}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {commentId ? "Guardar" : parentId ? "Responder" : "Publicar comentário"}
          </button>
        </div>
      </div>
    </div>
  );
}

function highlightMentions(body) {
  // Find @Name tokens and highlight them in blue.
  // Names with spaces: greedy until 2 words.
  const parts = [];
  const rx = /(@[\p{L}][\p{L}.-]*(?:\s[\p{L}][\p{L}.-]*)?)/gu;
  let last = 0;
  let m;
  while ((m = rx.exec(body))) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    parts.push(<span key={m.index} className="text-[#1F3864] font-medium">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts.length ? parts : body;
}

function CommentItem({ c, currentUser, isReply, onReplyPosted, onUpdated, onDeleted, recordType, recordId }) {
  const [showReply, setShowReply] = useState(false);
  const [editing, setEditing] = useState(false);
  const isAuthor = currentUser?.id === c.author_user_id;
  const isAdmin = currentUser?.role === "admin";
  const createdMs = new Date(c.created_at).getTime();
  const canEdit = isAuthor && Date.now() - createdMs < EDIT_WINDOW_MS;
  const role = c.author?.role || c.author_role || "";
  const roleColor = ROLE_COLORS[role] || "#6C757D";
  const showAbsolute = Date.now() - createdMs > 24 * 60 * 60 * 1000;
  const stamp = showAbsolute ? fmtDateTime(c.created_at) : fmtRelative(c.created_at);

  const onDelete = async () => {
    if (!confirm("Apagar este comentário?")) return;
    try {
      await api.delete(`/comments/${c.id}`);
      onDeleted(c.id);
    } catch (e) {
      alert(formatError(e.response?.data?.detail) || "Falha ao apagar");
    }
  };

  return (
    <div
      id={`comment-${c.id}`}
      className={`flex gap-2 ${isReply ? "ml-8 pl-3 border-l-2 border-[#E5E7EB]" : ""}`}
      data-testid={`comment-${c.id}`}
    >
      <div
        className="shrink-0 w-7 h-7 rounded-full text-white text-[11px] font-bold flex items-center justify-center mt-0.5"
        style={{ backgroundColor: roleColor }}
        title={role}
      >
        {initials(c.author?.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#1A1A2E]">{c.author?.name || c.author_name}</span>
          <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{role}</span>
          <span className="text-[10px] text-[#94A3B8]" title={fmtDateTime(c.created_at)}>{stamp}</span>
          {c.edited && (
            <span className="text-[10px] text-[#94A3B8] italic" title={c.edited_at}>· editado</span>
          )}
        </div>
        {editing ? (
          <div className="mt-1">
            <CommentEditor
              recordType={recordType}
              recordId={recordId}
              commentId={c.id}
              initialBody={c.body}
              onPosted={(updated) => {
                setEditing(false);
                onUpdated(updated);
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        ) : (
          <>
            <div className="text-sm text-[#1A1A2E] whitespace-pre-wrap break-words mt-0.5">
              {highlightMentions(c.body)}
            </div>
            {c.attachments && c.attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {c.attachments.map((a) => (
                  <a
                    key={a.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${a.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs bg-[#F4F6F8] hover:bg-[#E2E8F0] px-2 py-1 rounded-sm text-[#1F3864]"
                    data-testid={`comment-attachment-${a.id}`}
                  >
                    <FileText className="w-3 h-3" />
                    {a.original_filename}
                    <Download className="w-3 h-3" />
                  </a>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3 mt-1">
              {!isReply && (
                <button
                  onClick={() => setShowReply((v) => !v)}
                  className="text-[11px] text-[#6C757D] hover:text-[#1F3864] inline-flex items-center gap-1"
                  data-testid={`comment-reply-${c.id}`}
                >
                  <Reply className="w-3 h-3" /> Responder
                </button>
              )}
              {canEdit && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="text-[11px] text-[#6C757D] hover:text-[#1F3864] inline-flex items-center gap-1"
                  data-testid={`comment-edit-${c.id}`}
                >
                  <Edit2 className="w-3 h-3" /> Editar
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={onDelete}
                  className="text-[11px] text-[#DC3545] hover:underline inline-flex items-center gap-1"
                  data-testid={`comment-delete-${c.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Apagar
                </button>
              )}
            </div>
            {showReply && (
              <div className="mt-2">
                <CommentEditor
                  recordType={recordType}
                  recordId={recordId}
                  parentId={c.id}
                  onPosted={(reply) => {
                    setShowReply(false);
                    onReplyPosted(reply);
                  }}
                  onCancel={() => setShowReply(false)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function CommentThread({ recordType, recordId, compact = false }) {
  const { user } = useAuth();
  const [comments, setComments] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    api
      .get(`/comments?record_type=${recordType}&record_id=${recordId}`)
      .then((r) => setComments(r.data))
      .catch((e) => setErr(formatError(e.response?.data?.detail) || "Falha a carregar"));
  }, [recordType, recordId]);

  useEffect(() => { load(); }, [load]);

  const { roots, replies } = useMemo(() => {
    if (!comments) return { roots: [], replies: {} };
    const roots = comments.filter((c) => !c.parent_comment_id);
    const replies = {};
    for (const c of comments.filter((x) => x.parent_comment_id)) {
      (replies[c.parent_comment_id] ||= []).push(c);
    }
    return { roots, replies };
  }, [comments]);

  const onUpdated = (updated) => {
    setComments((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
  };
  const onDeleted = (id) => {
    setComments((arr) => arr.filter((x) => x.id !== id));
  };
  const onPosted = () => load();

  const readOnly = user?.role === "read_only_viewer";

  if (err) return <div className="text-sm text-[#DC3545]">{err}</div>;

  return (
    <div className={compact ? "" : "bg-white border border-gray-200 rounded-md p-4"} data-testid="comment-thread">
      {!compact && (
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3">
          Comentários ({comments ? comments.length : "…"})
        </h3>
      )}
      {comments === null ? (
        <div className="flex items-center gap-2 text-sm text-[#6C757D]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> A carregar…
        </div>
      ) : comments.length === 0 ? (
        <div className="text-sm text-[#6C757D] mb-3">Sem comentários. Seja o primeiro a comentar.</div>
      ) : (
        <div className="space-y-4 mb-4">
          {roots.map((c) => (
            <div key={c.id}>
              <CommentItem c={c} currentUser={user} recordType={recordType} recordId={recordId} onReplyPosted={onPosted} onUpdated={onUpdated} onDeleted={onDeleted} />
              {(replies[c.id] || []).map((r) => (
                <div key={r.id} className="mt-2">
                  <CommentItem c={r} currentUser={user} isReply recordType={recordType} recordId={recordId} onReplyPosted={onPosted} onUpdated={onUpdated} onDeleted={onDeleted} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {!readOnly && comments !== null && (
        <div className="border-t border-gray-100 pt-3">
          <CommentEditor recordType={recordType} recordId={recordId} onPosted={onPosted} />
        </div>
      )}
      {readOnly && (
        <div className="text-xs text-[#94A3B8] italic">Modo só-leitura — não pode publicar comentários.</div>
      )}
    </div>
  );
}
